import React, { FC, memo, useState, useCallback } from 'react';
import { WarehouseForBooking, getWorkingScheduleString } from 'entities/Warehouse';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { ReactComponent as CallIcon } from 'shared/assets/icons/newIcons/CallIcon.svg';
import { ReactComponent as SendEmailIcon } from 'shared/assets/icons/newIcons/SendEmailIcon.svg';
import { ReactComponent as TimeIcon } from 'shared/assets/icons/newIcons/TimeIcon.svg';

import { Button } from 'shared/ui/Button';

import { Modal } from 'shared/ui/Modal';
import { UnifiedWarehouseMap } from 'widgets/UnifiedWarehouseMap';
import WarehouseDefaultImage from 'shared/assets/images/WarehouseDefaultImage.png';

interface ModernWarehouseInfoCardProps {
  warehouseInfo: WarehouseForBooking;
}

export const ModernWarehouseInfoCard: FC<ModernWarehouseInfoCardProps> = memo((props) => {
  const { warehouseInfo } = props;
  const { phone, email, workingSchedule, name, imageUrl } = warehouseInfo;

  const [isMapModalOpen, setIsMapModalOpen] = useState(false);

  const { t } = useAppTranslation(['booking', 'common']);

  const workingScheduleString = getWorkingScheduleString(workingSchedule, t);

  const handleShowOnMap = useCallback((): void => {
    setIsMapModalOpen(true);
  }, []);

  const handleCloseMapModal = useCallback((): void => {
    setIsMapModalOpen(false);
  }, []);

  const warehouseImage = imageUrl || WarehouseDefaultImage;

  return (
    <>
      <div className="bg-white rounded-lg p-4 flex-shrink-0">
        {/* Warehouse Image */}
        <div className="mb-4">
          <img src={warehouseImage} alt={name || 'Warehouse'} className="w-full h-32 object-cover rounded-lg" />
        </div>

        {/* Warehouse Name */}
        <div className="font-semibold text-xl mb-3 font-tektur text-primary font-tektur">{name}</div>

        {/* Contact Info */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-grey text-sm">
            <CallIcon className="w-5 h-5 mr-2 text-grey" />
            <span>{phone}</span>
          </div>

          <div className="flex items-center text-grey text-sm">
            <SendEmailIcon className="w-5 h-5 mr-2 text-grey" />
            <span>{email}</span>
          </div>

          <div className="flex items-center text-grey text-sm">
            <TimeIcon className="w-5 h-5 mr-2 text-grey" />
            <span>{workingScheduleString}</span>
          </div>
        </div>

        {/* Size Tags */}
        {/* <div className="mb-4">
          <ModernSizeGroupsTags sizeCodes={sizeCodes} showTooltip={false} />
        </div> */}

        {/* Available Boxes */}
        {/* <div className="text-grey text-md mb-2">{t('{{availableBoxesAmount}} boxes free', { availableBoxesAmount })}</div> */}

        {/* Action */}
        <div className="flex flex-col space-y-3">
          <Button onClick={handleShowOnMap} className="w-full" theme="primary">
            {t('Show on map')}
          </Button>
        </div>
      </div>

      {/* Map Modal */}
      <Modal isOpen={isMapModalOpen} onClose={handleCloseMapModal} width={900} centered>
        {/* Modal Header */}
        <div className="flex justify-start items-center mb-4">
          <div className="font-semibold text-2xl text-primary font-tektur">{t('Warehouse location')}</div>
        </div>

        {/* Map Container */}
        <div className="relative w-full h-[500px] rounded-lg overflow-hidden border border-gray-200">
          <UnifiedWarehouseMap warehouseInfo={warehouseInfo} mapId="warehouseMapModal" zoom={15} withMarkerPopups={true} />
        </div>
      </Modal>
    </>
  );
});
